import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import { aiIntegrationFAQs } from '../data/faqs';
import { useNavigate } from 'react-router-dom';

const AIIntegration = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'AI Integration Services - Machine Learning & Automation | OctalChip',
    description: 'AI-powered solutions including machine learning models, chatbots, and intelligent automation. We integrate AI to enhance user experience and automate business processes.',
    keywords: 'AI integration, machine learning, chatbots, predictive analytics, process automation, AI development',
    canonical: 'https://www.octalchip.com/services/ai-integration',
    faqData: aiIntegrationFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI Integration Services",
      "description": "AI-powered solutions including machine learning models, chatbots, and intelligent automation",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const features = [
    {
      title: 'Machine Learning Models',
      description: 'Custom ML models for prediction, classification, and pattern recognition tailored to your needs',
      icon: 'Brain'
    },
    {
      title: 'Intelligent Chatbots',
      description: 'AI-powered chatbots with natural language processing for customer support and engagement',
      icon: 'MessageCircle'
    },
    {
      title: 'Predictive Analytics',
      description: 'Data-driven insights and forecasts to make informed business decisions',
      icon: 'TrendingUp'
    },
    {
      title: 'Process Automation',
      description: 'Automate repetitive tasks and workflows to increase efficiency and reduce costs',
      icon: 'Settings'
    },
    {
      title: 'Computer Vision',
      description: 'Image and video analysis for object detection, recognition, and content moderation',
      icon: 'Eye'
    },
    {
      title: 'Natural Language Processing',
      description: 'Text analysis, sentiment analysis, and language understanding for better insights',
      icon: 'FileText'
    }
  ];

  const technologies = [
    { name: 'Python', category: 'Language', description: 'Primary language for AI and ML development' },
    { name: 'TensorFlow', category: 'Framework', description: 'Google\'s open-source ML framework' },
    { name: 'PyTorch', category: 'Framework', description: 'Facebook\'s deep learning framework' },
    { name: 'OpenAI API', category: 'Service', description: 'GPT models for natural language processing' },
    { name: 'Scikit-learn', category: 'Library', description: 'Machine learning library for Python' },
    { name: 'Pandas', category: 'Library', description: 'Data manipulation and analysis' },
    { name: 'Keras', category: 'Framework', description: 'High-level neural networks API' },
    { name: 'Hugging Face', category: 'Platform', description: 'Pre-trained models and transformers' },
    { name: 'OpenCV', category: 'Library', description: 'Computer vision and image processing' }
  ];

  const useCases = [
    {
      title: 'Customer Support Chatbots',
      description: '24/7 AI assistants that handle customer queries, provide instant responses, and escalate when needed',
      icon: 'MessageSquare'
    },
    {
      title: 'Recommendation Systems',
      description: 'Personalized product and content recommendations based on user behavior and preferences',
      icon: 'Star'
    },
    {
      title: 'Fraud Detection',
      description: 'Real-time fraud detection and prevention using anomaly detection and pattern recognition',
      icon: 'Shield'
    },
    {
      title: 'Predictive Maintenance',
      description: 'Predict equipment failures and schedule maintenance to reduce downtime and costs',
      icon: 'Wrench'
    },
    {
      title: 'Image Recognition',
      description: 'Automated image classification, object detection, and content moderation',
      icon: 'Image'
    },
    {
      title: 'Sentiment Analysis',
      description: 'Analyze customer feedback, reviews, and social media to understand public sentiment',
      icon: 'Heart'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Problem Analysis',
      description: 'Understand your business challenges and identify AI opportunities for improvement',
      icon: 'Search'
    },
    {
      step: '02',
      title: 'Data Preparation',
      description: 'Collect, clean, and prepare data for training and validation',
      icon: 'Database'
    },
    {
      step: '03',
      title: 'Model Development',
      description: 'Design, train, and optimize ML models using appropriate algorithms',
      icon: 'Code'
    },
    {
      step: '04',
      title: 'Testing & Validation',
      description: 'Test models with real-world data and validate accuracy and performance',
      icon: 'CheckCircle'
    },
    {
      step: '05',
      title: 'Integration & Deployment',
      description: 'Integrate AI models into your existing systems and deploy to production',
      icon: 'Rocket'
    },
    {
      step: '06',
      title: 'Monitoring & Optimization',
      description: 'Continuously monitor performance and retrain models for better accuracy',
      icon: 'BarChart'
    }
  ];

  const benefits = [
    'Automate repetitive tasks and workflows',
    'Improve decision-making with data-driven insights',
    'Enhance customer experience with AI assistants',
    'Reduce operational costs through automation',
    'Increase accuracy and reduce human errors',
    'Scale operations without proportional cost increase',
    'Gain competitive advantage with AI capabilities',
    'Personalize user experiences at scale'
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Enhanced Hero Section */}
      <section id="hero" className="relative py-8 sm:py-12 md:py-16 lg:py-20 xl:py-32 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00d690 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-xl sm:rounded-2xl mb-6 sm:mb-8 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <Icon name="Cpu" size={32} className="sm:w-12 sm:h-12 md:w-14 md:h-14 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-800 mb-4 sm:mb-6 leading-tight px-4">
              AI Integration
              <span className="block text-[#00d690] mt-1 sm:mt-2">Services</span>
            </h1>
            <div className="w-20 sm:w-24 md:w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-6 sm:mb-8"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
              Harness the power of artificial intelligence to transform your business. We build intelligent solutions 
              that automate processes, enhance decision-making, and deliver exceptional user experiences.
            </p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 px-4">
              <Button 
                variant="primary" 
                onClick={() => navigate('/services#share-your-requirements')}
                className="px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                Start Your Project
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/services#services')}
                className="px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg border-2"
              >
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-6 sm:py-8 md:py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">10+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">AI Projects</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">3-8</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">95%</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">Accuracy Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#00d690] mb-1 sm:mb-2">24/7</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">AI Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">AI Capabilities</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive AI solutions to transform your business operations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:from-[#00d690] group-hover:to-[#00b876] transition-all duration-300">
                  <Icon name={feature.icon} size={20} className="sm:w-6 sm:h-6 md:w-7 md:h-7 text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-[#00d690] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">AI Technologies</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Cutting-edge AI frameworks and tools we use
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-7xl mx-auto">
            {technologies.map((tech, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">
                    {tech.name}
                  </span>
                  <span className="text-xs font-medium text-[#00d690] bg-[#00d690]/10 px-2 py-1 rounded">
                    {tech.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tech.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#00d690]/5 to-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">AI Use Cases</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Real-world AI applications that drive business value
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group border-l-4 border-[#00d690]"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-[#00d690] transition-all duration-300">
                  <Icon name={useCase.icon} size={20} className="sm:w-6 sm:h-6 md:w-7 md:h-7 text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-[#00d690] transition-colors">
                  {useCase.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Our AI Development Process</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              A systematic approach to building effective AI solutions
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d690] to-[#00b876]"></div>
              
              <div className="space-y-8 sm:space-y-10 md:space-y-12">
                {processSteps.map((step, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-start gap-4 sm:gap-6">
                    <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base md:text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    
                    <div className="flex-1 bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                      <div className="flex items-center mb-3 sm:mb-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                          <Icon name={step.icon} size={18} className="sm:w-5 sm:h-5 md:w-6 md:h-6 text-[#00d690]" />
                        </div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Benefits of AI Integration</h2>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 sm:gap-4 bg-white/5 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-[#00d690] rounded-full flex items-center justify-center">
                    <Icon name="Check" size={14} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <p className="text-sm sm:text-base md:text-lg text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-6 sm:mb-8 md:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
              Ready to Integrate AI into Your Business?
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 px-4">
              Let's discuss how AI can transform your operations and drive innovation. 
              Get a free consultation and discover the possibilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <button
                onClick={() => navigate('/services#share-your-requirements')}
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
              >
                Start Your Project
              </button>
              <button
                onClick={() => navigate('/services#services')}
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 text-sm sm:text-base"
              >
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={aiIntegrationFAQs}
        title="AI Integration FAQs"
        description="Common questions about AI integration, machine learning, chatbots, and automation."
      />

      <Footer />
    </div>
  );
};

export default AIIntegration;
